import { NextPage } from 'next';
import { reduxWrapper } from 'store';

import { fetchArticleOrTopic } from 'state/actions/contentHubActions';
import { CacheControlHeaders } from 'constants/Global';

import get from 'lodash/get';

import ArticlePageView from 'views/ArticlePageView';
import TopicPageView from 'views/TopicPageView';

import { Article, Topic as ITopic, ErrorPage } from 'types';

interface InitialProps {
  article: Article | null;
  errorPage: ErrorPage;
  isArticlePage: boolean;
  page: ITopic | null;
}

type Props = InitialProps;

const TopicPage: NextPage<Props> = ({
  article,
  errorPage,
  isArticlePage,
  page,
}) => {
  if (isArticlePage) {
    return <ArticlePageView article={article} errorPage={errorPage} />;
  }

  return <TopicPageView page={page} errorPage={errorPage} />;
};

export const getServerSideProps = reduxWrapper.getServerSideProps(
  ({ store, params, query, res }) => {
    res.setHeader('Cache-Control', CacheControlHeaders.MAX_AGE_1_MONTH);
    const slug = get(params, 'contentHubSlugs', []).slice(-1)[0];
    const preview =
      (get(query, 'article_preview', '') as string) ||
      (get(query, 'topic_preview', '') as string);
    const errorPageFromStore = store.getState().globalSettings.errorPage;

    if (!slug) {
      return {
        props: {
          article: null,
          errorPage: errorPageFromStore,
          isArticlePage: true,
          page: null,
        },
      };
    }

    const article = store.getState().article[`/${slug}`] || null;

    if (!preview && article) {
      return {
        props: {
          article: article,
          errorPage: errorPageFromStore,
          isArticlePage: true,
          page: null,
          slug: `/${slug}`,
        },
      };
    }

    const topicPage = store.getState().topic.topicsBySlug[`/${slug}`] || null;

    if (!preview && topicPage) {
      return {
        props: {
          article: null,
          errorPage: errorPageFromStore,
          isArticlePage: false,
          page: topicPage,
          slug: `/${slug}`,
        },
      };
    }

    const action = fetchArticleOrTopic(`/${slug}`, preview);
    store.dispatch(action);

    return action.payload.then((response) => {
      if (!response) {
        if (res) {
          res.statusCode = 404;
        }

        return {
          props: {
            article: null,
            errorPage: errorPageFromStore,
            isArticlePage: true,
            page: null,
          },
        };
      }

      if (response.type === 'article') {
        return {
          props: {
            article: response,
            errorPage: errorPageFromStore,
            isArticlePage: true,
            page: null,
            slug: `/${slug}`,
          },
        };
      }

      if (response.type === 'topic') {
        return {
          props: {
            article: null,
            errorPage: errorPageFromStore,
            isArticlePage: false,
            page: response,
            slug: `/${slug}`,
          },
        };
      }
    });
  }
);

export default reduxWrapper.withRedux(TopicPage);
